import {
  InputRequirement,
  SurveyQuestion,
  UserInput,
  UserInputAnswer,
} from "@intelligems/ig-types";

/**
 * Pure state helpers for the survey card, kept apart from React so the
 * stepper's rules — what counts as answered, where answering moves you, what
 * payload a finished card submits — are unit-testable without a provider tree.
 */

/** Longest prompt the card header will show verbatim — about three lines. */
export const SURVEY_CARD_TITLE_MAX_CHARS = 240;
export const SURVEY_CARD_TITLE_MAX_LINES = 3;

/** Header title when the ask's prompt is too long to be a card title. */
export const SURVEY_CARD_TITLE_FALLBACK = "Answer the question above";

/**
 * What the card header shows for a question. The header is a title of at
 * most about three lines, not a message body: a prompt within that budget is
 * shown as-is; anything longer (a legacy ask whose `prompt` is a whole
 * proposal, say) is replaced by a pointer at the transcript bubble that
 * already renders the full prompt, so a blob of text can never land in the
 * component.
 */
export function surveyCardTitle(prompt: string): string {
  const trimmed = prompt.trim();
  if (trimmed.length === 0) return SURVEY_CARD_TITLE_FALLBACK;
  const lines = trimmed.split("\n").length;
  if (
    trimmed.length > SURVEY_CARD_TITLE_MAX_CHARS ||
    lines > SURVEY_CARD_TITLE_MAX_LINES
  ) {
    return SURVEY_CARD_TITLE_FALLBACK;
  }
  return trimmed;
}

/**
 * Every requirement the survey card renders, normalized to a question list.
 * A `survey` brings its own questions; a legacy `choice` or `confirmation`
 * is ONE question (the requirement's prompt) so the card renders identically
 * with the stepper simply absent. Anything else is not a card ask.
 */
export function deriveSurveyQuestions(
  requirement: InputRequirement
): SurveyQuestion[] | null {
  const { userInput } = requirement;
  switch (userInput.type) {
    case "survey":
      return userInput.questions;
    case "choice":
      if (userInput.options.length === 0) return null;
      return [
        {
          prompt: requirement.prompt ?? "",
          options: userInput.options,
          multiSelect: userInput.multiSelect,
        },
      ];
    case "confirmation":
      return [
        {
          prompt: requirement.prompt ?? "",
          options: [
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
          ],
        },
      ];
    default:
      return null;
  }
}

/**
 * Where answering question `from` moves the stepper: the next unanswered
 * question scanning forward, wrapping to the front for anything skipped by
 * going back. `null` means every question is answered — time to submit.
 */
export function nextUnansweredIndex(
  answers: Record<number, UserInputAnswer>,
  from: number,
  total: number
): number | null {
  for (let index = from + 1; index < total; index++) {
    if (!answers[index]) return index;
  }
  for (let index = 0; index < from; index++) {
    if (!answers[index]) return index;
  }
  return null;
}

/**
 * The canonical submission a completed question card sends: one entry per
 * question, in question order. The same shape covers one question and many.
 */
export function buildSurveyUserInput(
  questions: SurveyQuestion[],
  answers: Record<number, UserInputAnswer>
): UserInput {
  return {
    type: "answers",
    answers: questions.map(
      (question, index) =>
        answers[index] ?? { prompt: question.prompt, choices: [] }
    ),
  };
}

/**
 * What answering the current question does to the survey: records the answer,
 * and either advances to the next unanswered question (`submission: null`) or
 * — when that was the last unanswered one — produces the single `answers`
 * user input to submit.
 */
export function recordSurveyAnswer(
  questions: SurveyQuestion[],
  answers: Record<number, UserInputAnswer>,
  currentIndex: number,
  answer: UserInputAnswer
): {
  answers: Record<number, UserInputAnswer>;
  currentIndex: number;
  submission: UserInput | null;
} {
  const nextAnswers = { ...answers, [currentIndex]: answer };
  const next = nextUnansweredIndex(nextAnswers, currentIndex, questions.length);
  if (next === null) {
    return {
      answers: nextAnswers,
      currentIndex,
      submission: buildSurveyUserInput(questions, nextAnswers),
    };
  }
  return { answers: nextAnswers, currentIndex: next, submission: null };
}
